import type { ReactNode } from 'react'
import styles from './Breadcrumb.module.css'

export interface Crumb {
  label: string
  /** Omit on the last crumb — the page you are already on isn't a link. */
  href?: string
}

export interface BreadcrumbProps {
  items: Crumb[]
  /**
   * Renders a linked crumb. Supplied by the app so the component stays free of
   * any router: the org pages hand it a react-router <Link>, a plain page can
   * hand it an <a>, and neither has to agree with the other.
   */
  renderLink?: (crumb: Crumb, children: ReactNode) => ReactNode
  className?: string
  /** Accessible name for the nav landmark. Defaults to "Breadcrumb". */
  label?: string
}

/**
 * A trail back up from a nested page.
 *
 * Deliberately a trail rather than a bare "Back" button. Back tells you nothing
 * about where you are, and it lies as soon as someone arrives by link rather
 * than by clicking through — which is the normal case for a shared competition
 * page.
 *
 * The last item is rendered as plain text and marked aria-current="page": it is
 * where you already are, so linking it would be a no-op that still reads as a
 * link to a screen reader.
 */
export function Breadcrumb({ items, renderLink, className, label = 'Breadcrumb' }: BreadcrumbProps) {
  if (items.length === 0) return null

  return (
    <nav aria-label={label} className={[styles.nav, className].filter(Boolean).join(' ')}>
      <ol className={styles.list}>
        {items.map((crumb, i) => {
          const isLast = i === items.length - 1
          const content = <span className={styles.label}>{crumb.label}</span>
          return (
            <li key={`${crumb.label}-${i}`} className={styles.item}>
              {isLast || !crumb.href ? (
                <span className={styles.current} aria-current={isLast ? 'page' : undefined}>
                  {content}
                </span>
              ) : (
                <span className={styles.link}>
                  {renderLink ? renderLink(crumb, content) : <a href={crumb.href}>{content}</a>}
                </span>
              )}
              {/* Decorative: the list structure already conveys the nesting, so
                  a screen reader shouldn't read a slash between every level. */}
              {!isLast && <span className={styles.separator} aria-hidden="true">/</span>}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
